import { Trans } from "@lingui/react/macro"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"
import { z } from "zod"
import { shelfCodesQueryOptions } from "@/features/bill-of-materials/hooks/useShelfCodes"
import { CodingGapsView } from "@/features/list-of-materials/components/coding/CodingGapsView"
import { CodingGroupList } from "@/features/list-of-materials/components/coding/CodingGroupList"
import { CodingGroupView } from "@/features/list-of-materials/components/coding/CodingGroupView"
import { ExportExcelButton } from "@/features/list-of-materials/components/form/ExportExcelButton"
import { HowLink } from "@/features/list-of-materials/components/how-it-works/HowLink"
import { Chip } from "@/features/list-of-materials/components/ui/Chip"
import { PageHead, PageStatus } from "@/features/list-of-materials/components/ui/PageHead"
import { TabLink, Tabs } from "@/features/list-of-materials/components/ui/Tabs"
import { summarizeCoding } from "@/features/list-of-materials/core/coding"
import { groupCodingGaps } from "@/features/list-of-materials/core/codingGaps"
import { formatCount } from "@/features/list-of-materials/core/format"
import { useCanEditLom } from "@/features/list-of-materials/hooks/useCanEditLom"
import {
  lomCodingGapsQueryOptions,
  lomCodingQueryOptions,
} from "@/features/list-of-materials/hooks/useLomData"
import {
  lomVersionsQueryOptions,
  useSelectedLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"
import { exportLomCodingFn } from "@/features/list-of-materials/server/lomCoding"

export const Route = createFileRoute("/(authenticated)/list-of-materials/coding")({
  validateSearch: z.object({
    group: z.string().optional(),
    side: z.enum(["gondola", "wall"]).optional(),
    view: z.enum(["tables", "missing"]).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, deps }) => {
    const { selected } = await context.queryClient.ensureQueryData(
      lomVersionsQueryOptions(deps.version),
    )
    if (!selected) return
    await Promise.all([
      context.queryClient.ensureQueryData(lomCodingQueryOptions(selected.id)),
      context.queryClient.ensureQueryData(shelfCodesQueryOptions(selected.id)),
    ])
  },
  component: CodingPage,
})

function CodingPage() {
  const { version, versionSearch } = useSelectedLomVersion()
  const search = Route.useSearch()
  const canEdit = useCanEditLom()
  const { data: coding } = useSuspenseQuery(lomCodingQueryOptions(version.id))
  const { data: shelfCodes } = useSuspenseQuery(shelfCodesQueryOptions(version.id))

  // Setup variants never go in the coding: their base does.
  const knownCodes = useMemo(
    () => shelfCodes.filter((c) => c.variantOfId === null).map((c) => c.code),
    [shelfCodes],
  )
  const codeColors = useMemo(
    () => new Map(shelfCodes.map((c) => [c.code.toUpperCase(), c.colorHex])),
    [shelfCodes],
  )
  const variantBases = useMemo(() => {
    const byId = new Map(shelfCodes.map((c) => [c.id, c.code]))
    return new Map(
      shelfCodes.flatMap((c) =>
        c.variantOfId === null ? [] : [[c.code.toUpperCase(), byId.get(c.variantOfId) ?? ""]],
      ),
    )
  }, [shelfCodes])
  const summaries = useMemo(
    () =>
      summarizeCoding(
        coding.groups,
        coding.templates,
        new Set(knownCodes.map((c) => c.toUpperCase())),
      ),
    [coding, knownCodes],
  )
  const selected = summaries.find((s) => s.group.id === search.group) ?? summaries[0]
  const side = search.side ?? selected?.sides[0] ?? "gondola"
  const unknown = summaries.reduce((sum, s) => sum + s.unknown, 0)
  const view = search.view ?? "tables"
  const { data: gaps } = useQuery(lomCodingGapsQueryOptions(version.id))
  const gapCount = gaps ? groupCodingGaps(gaps.rows).length : undefined

  return (
    <>
      <PageHead
        help={<HowLink topic="coding" />}
        title={<Trans>Coding</Trans>}
        sub={
          <Trans>
            Per merchandise group, side and group size: the shelf code of every meter, from the
            Product Group Coding.
          </Trans>
        }
        right={
          <PageStatus>
            {unknown === 0 ? (
              <Chip tone="ok" dot>
                <Trans>Complete</Trans>
              </Chip>
            ) : (
              <Chip tone="warn" dot>
                <Trans>{unknown} unknown codes</Trans>
              </Chip>
            )}
            <Trans>
              {formatCount(coding.groups.length)} groups · {formatCount(coding.templates.length)}{" "}
              cells
            </Trans>
            <ExportExcelButton
              exportFile={() => exportLomCodingFn({ data: { configVersionId: version.id } })}
            />
          </PageStatus>
        }
      />
      <Tabs>
        <TabLink
          to="/list-of-materials/coding"
          search={{ ...versionSearch, group: search.group, side: search.side }}
          current={view === "tables"}
        >
          <Trans>Tables</Trans>
        </TabLink>
        <TabLink
          to="/list-of-materials/coding"
          search={{ ...versionSearch, view: "missing" }}
          current={view === "missing"}
          count={gapCount}
        >
          <Trans>Missing sizes</Trans>
        </TabLink>
      </Tabs>
      {view === "missing" ? (
        <CodingGapsView />
      ) : (
        <div className="grid grid-cols-[280px_1fr] items-start gap-5 max-[900px]:grid-cols-1">
          <CodingGroupList
            summaries={summaries}
            selectedId={selected?.group.id}
            version={versionSearch.version}
          />
          {selected ? (
            <CodingGroupView
              key={`${selected.group.id}-${side}`}
              summary={selected}
              templates={coding.templates}
              side={side}
              knownCodes={knownCodes}
              codeColors={codeColors}
              variantBases={variantBases}
              configVersionId={version.id}
              version={versionSearch.version}
              canEdit={canEdit}
            />
          ) : (
            <p className="text-lom-ink-3">
              <Trans>This version has no merchandise groups.</Trans>
            </p>
          )}
        </div>
      )}
    </>
  )
}
